import React, { useCallback } from "react";
import { mergeProps, useFocusRing } from "react-aria";
import styled, { css } from "styled-components";
import { Capsule } from "~/applications/OfferSearch/Domain/DestinationPage";
import { heading6, subtitle2 } from "~/guidelines/Typography";
import CapsuleBox from "./Capsule";

const Container = styled.div<{
  $isSelected: boolean;
  $isFocusVisible: boolean;
  $isDisabled: boolean;
  $isTeasing: boolean;
}>`
  cursor: ${({ $isDisabled }) => ($isDisabled ? `default` : `pointer`)};
  margin: 0;
  padding: 0;

  background-color: ${({ theme }) => theme.color.background.lightFirst};
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected
        ? theme.color.line.lightPrimary
        : theme.color.line.lightSecond};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => theme.spacing(5)};

  transition:
    box-shadow ease-out 250ms,
    border ease-out 250ms,
    font-weight ease-out 250ms;

  ${({ $isSelected, $isDisabled }) =>
    !$isSelected && !$isDisabled
      ? css`
          &:hover {
            box-shadow: ${({ theme }) => theme.shadow.navigation};
          }
        `
      : null};

  ${({ $isSelected, $isDisabled }) =>
    $isSelected && !$isDisabled
      ? css`
          border: 1px solid ${({ theme }) => theme.color.brand.primary};
          box-shadow: ${({ theme }) => theme.shadow.navigation};
        `
      : null};

  outline: ${({ $isFocusVisible, $isDisabled, theme }) =>
    $isFocusVisible && !$isDisabled
      ? `2px solid ${theme.color.brand.primary};`
      : "none"};
  box-shadow: ${({ $isFocusVisible, $isDisabled, theme }) =>
    $isFocusVisible && !$isDisabled ? theme.shadow.navigation : "none"};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TeasingText = styled.div`
  ${subtitle2};
  color: ${({ theme }) => theme.color.text.lightTertiary};
`;

const DestinationName = styled.div`
  ${heading6};
`;

export type DestinationButtonProps = {
  as?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  isFocusVisible?: boolean;
  teasingText?: string;
  destinationName: string;
  onSelectDestination?: () => void;
  capsule?: Capsule | null;
};

const DestinationButton = React.forwardRef<HTMLElement, DestinationButtonProps>(
  function DestinationButtonForwardingRef(
    {
      as = "div",
      isSelected = false,
      isDisabled = false,
      teasingText,
      destinationName,
      onSelectDestination,
      ...props
    },
    forwardedRef,
  ) {
    const { isFocusVisible, focusProps } = useFocusRing();

    const handleClick = useCallback(() => {
      if (onSelectDestination === undefined) {
        return;
      }

      onSelectDestination();
    }, [onSelectDestination]);

    return (
      <Container
        {...mergeProps(props, focusProps)}
        as={as as any}
        ref={forwardedRef}
        $isSelected={isSelected}
        $isFocusVisible={isFocusVisible}
        $isDisabled={isDisabled}
        $isTeasing={teasingText !== undefined}
        onClick={handleClick}
      >
        {props.capsule && <CapsuleBox capsule={props.capsule} />}
        <ContentWrapper>
          <DestinationName>{destinationName}</DestinationName>
          {teasingText && <TeasingText>{teasingText}</TeasingText>}
        </ContentWrapper>
      </Container>
    );
  },
);

export default DestinationButton;
